import { PointOfInterestTemplateField } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import CalculateIcon from '@mui/icons-material/Calculate';
import CloseIcon from '@mui/icons-material/Close';
import FunctionsIcon from '@mui/icons-material/Functions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RuleIcon from '@mui/icons-material/Rule';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { PageLoader } from '../../../shared/PageLoader';
import { FieldLogicConfigurator } from '../../Assembly/FieldLogicConfigurator';
import { selectedPOIType } from '../../state';

export const PoiTemplateEngineKitPanel = () => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFieldForConfig, setSelectedFieldForConfig] =
    useState<PointOfInterestTemplateField | null>(null);

  // Get fields from the selected POI template
  const fields = useMemo(() => {
    return selectedType?.fields ?? [];
  }, [selectedType]);

  // Filter fields that can have logic/calculation (for stats)
  const configurableFields = useMemo(() => {
    if (!fields) return [];
    return fields.filter(
      (field) =>
        field.shouldEvaluateLogic ||
        field.shouldEvaluateMath ||
        field.shouldEvaluateLogicPostCalculation ||
        field.logicRule ||
        field.calculatedBy
    );
  }, [fields]);

  // Search functionality
  const filteredFields = useMemo(() => {
    if (!fields) return [];
    const q = searchTerm.trim().toLowerCase();
    return fields.filter((field) => (q ? (field.name ?? '').toLowerCase().includes(q) : true));
  }, [fields, searchTerm]);

  // Get field type icon
  const getFieldTypeIcon = (field: PointOfInterestTemplateField) => {
    if (field.shouldEvaluateMath || field.calculatedBy) {
      return <CalculateIcon sx={{ color: abyssColors.tertiary[500] }} />;
    }
    if (field.shouldEvaluateLogic || field.logicRule) {
      return <RuleIcon sx={{ color: abyssColors.success[500] }} />;
    }
    if (field.shouldEvaluateLogicPostCalculation) {
      return <FunctionsIcon sx={{ color: abyssColors.warning[500] }} />;
    }
    return null;
  };

  // Get field type badge
  const getFieldTypeBadge = (field: PointOfInterestTemplateField) => {
    const badges = [];
    if (field.shouldEvaluateMath || field.calculatedBy) {
      badges.push(
        <Chip
          key="math"
          label="Calculation"
          size="small"
          sx={{
            backgroundColor: abyssColors.tertiary[50],
            color: abyssColors.tertiary[700],
            fontSize: '0.75rem',
            height: '20px',
          }}
        />
      );
    }
    if (field.shouldEvaluateLogic || field.logicRule) {
      badges.push(
        <Chip
          key="logic"
          label="Logic"
          size="small"
          sx={{
            backgroundColor: abyssColors.success[50],
            color: abyssColors.success[700],
            fontSize: '0.75rem',
            height: '20px',
          }}
        />
      );
    }
    if (field.shouldEvaluateLogicPostCalculation) {
      badges.push(
        <Chip
          key="post-calc"
          label="Post-Calc Logic"
          size="small"
          sx={{
            backgroundColor: abyssColors.warning[50],
            color: abyssColors.warning[700],
            fontSize: '0.75rem',
            height: '20px',
          }}
        />
      );
    }
    return badges.length > 0 ? (
      <Stack direction="row" spacing={0.5}>
        {badges}
      </Stack>
    ) : null;
  };

  const handleFieldClick = (field: PointOfInterestTemplateField) => {
    setSelectedFieldForConfig(field);
  };

  const handleFieldUpdate = (updatedField: PointOfInterestTemplateField) => {
    // Update the field in the selected POI template
    setSelectedType({
      ...selectedType,
      fields: (selectedType?.fields ?? []).map((f) =>
        f.id === updatedField.id ? updatedField : f
      ),
    });
    setSelectedFieldForConfig(updatedField);
  };

  if (!selectedType) return <PageLoader />;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
        sx={{ mb: 2 }}
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          {`Template Engine Kit for ${selectedType?.name}`}
        </Typography>
      </Stack>

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        Configure logic rules and calculations for your POI template fields.
      </Alert>

      {/* Summary Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Chip
          label={`Total Fields: ${fields.length}`}
          sx={{ backgroundColor: abyssColors.neutral[100] }}
        />
        <Chip
          label={`Configured: ${configurableFields.length}`}
          sx={{ backgroundColor: abyssColors.primary[50], color: abyssColors.primary[700] }}
        />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Fields List Panel */}
        <Box
          sx={{
            width: '350px',
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${abyssColors.neutral[200]}`,
            pr: 2,
          }}
        >
          <TextField
            size="small"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Template Fields ({filteredFields.length})
          </Typography>

          <Stack spacing={1} sx={{ overflowY: 'auto', flex: 1 }}>
            {filteredFields.map((field) => {
              const isConfigured =
                field.shouldEvaluateLogic ||
                field.shouldEvaluateMath ||
                field.shouldEvaluateLogicPostCalculation ||
                field.logicRule ||
                field.calculatedBy;
              const isSelected = selectedFieldForConfig?.id === field.id;

              return (
                <Card
                  key={field.id}
                  onClick={() => handleFieldClick(field)}
                  elevation={0}
                  sx={{
                    cursor: 'pointer',
                    border: isSelected
                      ? `2px solid ${abyssColors.primary[500]}`
                      : `1px solid ${abyssColors.neutral[200]}`,
                    backgroundColor: isSelected
                      ? abyssColors.primary[50]
                      : isConfigured
                      ? abyssColors.neutral[50]
                      : 'white',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? abyssColors.primary[100]
                        : abyssColors.neutral[100],
                      borderColor: abyssColors.primary[300],
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {getFieldTypeIcon(field)}
                        <Typography
                          variant="body2"
                          fontWeight={isSelected ? 600 : 500}
                          sx={{ flex: 1 }}
                        >
                          {field.name}
                        </Typography>
                      </Stack>

                      {isConfigured && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {getFieldTypeBadge(field)}
                        </Stack>
                      )}

                      {field.key && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {field.key}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>

        {/* Configuration Panel */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {selectedFieldForConfig ? (
            <FieldLogicConfigurator
              field={selectedFieldForConfig as any}  
              template={selectedType as any}  
              templateType="poi"
              structureId="" // POI templates don't have structureId, handled differently
              onUpdate={handleFieldUpdate as any}  
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                height: '100%',
                color: abyssColors.neutral[400],
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <FunctionsIcon sx={{ fontSize: '4rem' }} />
                <Typography variant="h6">Select a field to configure</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a field from the list to view and edit its logic configuration
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
